import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';
import { matchesSearch } from '../common/search';
import { formatDurationLabel, getTaskTimeUnits } from '../common/taskProgress';

const { FiCalendar, FiFolders, FiClock, FiFlag } = FiIcons;

const CalendarView = ({
  tasks,
  projects,
  currentProjectId,
  onTaskEdit,
  updateTaskDates,
  onToggleComplete,
  searchQuery,
  tags
}) => {
  const { t } = useTranslation();
  const [viewType, setViewType] = useState('timeGridWeek');
  const { darkMode } = useTheme();

  // Filter tasks by project if a project is selected
  const filteredTasks = currentProjectId
    ? tasks.filter(task => task.projectId === currentProjectId)
    : tasks;

  // Convert tasks and subtasks to calendar events
  const calendarEvents = [];

  filteredTasks.forEach(task => {
    const project = projects.find(p => p.id === task.projectId);
    const projectColor = project?.color || '#3b82f6';

    const isMatch = matchesSearch(task, searchQuery, tags);

    if (task.dueDate && (task.startTime || task.isMultiDay) && !task.flexMode) {
      const startDate = new Date(task.dueDate);
      let endDate;
      if (task.isMultiDay && task.endDate) {
        endDate = new Date(task.endDate);
        endDate.setDate(endDate.getDate() + 1);
      } else if (task.startTime) {
        endDate = new Date(startDate);
        const [startHours, startMinutes] = task.startTime.split(':').map(Number);
        startDate.setHours(startHours, startMinutes, 0);
        endDate.setHours(startHours, startMinutes + (task.duration || 60), 0);
      } else {
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
      }

        calendarEvents.push({
          id: task.id,
          title: task.title,
          start: startDate,
          end: endDate,
          backgroundColor: projectColor,
          borderColor: projectColor,
          textColor: '#ffffff',
          allDay: task.isMultiDay,
          classNames: isMatch ? ['search-highlight'] : [],
          extendedProps: {
            description: task.description,
            priority: task.priority,
            completed: task.completed,
            projectId: task.projectId,
            projectName: project?.name || 'Inbox',
            task: task
          }
        });
    }

    if (task.flexMode && task.subTasks && task.subTasks.length > 0) {
      task.subTasks.forEach(st => {
        if (!st.dueDate) return;
        const subStart = new Date(st.dueDate);
        let subEnd;
        if (st.isMultiDay && st.endDate) {
          subEnd = new Date(st.endDate);
          subEnd.setDate(subEnd.getDate() + 1);
        } else {
          const [h, m] = (st.time || '00:00').split(':').map(Number);
          subStart.setHours(h, m, 0, 0);
          subEnd = new Date(subStart);
          subEnd.setMinutes(subEnd.getMinutes() + (st.duration || 0));
          if (!task.flexMode) {
            const parentEnd = task.endDate ? new Date(task.endDate) : null;
            if (parentEnd && subEnd > parentEnd) subEnd.setTime(parentEnd.getTime());
          }
        }

        const subMatch = matchesSearch(st, searchQuery, tags);
        calendarEvents.push({
          id: st.id,
          title: st.title,
          start: subStart,
          end: subEnd,
          backgroundColor: projectColor,
          borderColor: projectColor,
          textColor: '#ffffff',
          allDay: st.isMultiDay || false,
          classNames: subMatch ? ['search-highlight'] : [],
          extendedProps: { task: st, parentTaskId: task.id, parentTitle: task.title, completed: st.completed }
        });
      });
    }
  });

  // Handle event click
  const handleEventClick = (info) => {
    const eventTask = info.event.extendedProps.task;
    const parentId = info.event.extendedProps.parentTaskId;
    if (parentId) {
      const parent = tasks.find(t => t.id === parentId);
      if (parent) onTaskEdit(parent, eventTask.id);
    } else {
      onTaskEdit(eventTask);
    }
  };

  // Normalize a date to the nearest 30-minute mark
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setSeconds(0, 0);
    const minutes = d.getMinutes();
    d.setMinutes(minutes - (minutes % 30));
    return d;
  };

  const hasOverlap = (taskId, startDate, endDate) => {
    const moving = tasks.find(t => t.id === taskId);
    const movingAnti = moving?.antiOverlap;
    const dayStr = startDate.toISOString().split('T')[0];
    const startMins = startDate.getHours() * 60 + startDate.getMinutes();
    const endMins = endDate.getHours() * 60 + endDate.getMinutes();
    return tasks.some(t => {
      if (
        t.id === taskId ||
        t.flexMode ||
        t.isMultiDay ||
        t.dueDate?.split('T')[0] !== dayStr ||
        !t.startTime ||
        !t.duration
      ) {
        return false;
      }
      const [h, m] = t.startTime.split(':').map(Number);
      const tStart = h * 60 + m;
      const tEnd = tStart + t.duration;
      const overlap = startMins < tEnd && endMins > tStart;
      return overlap && (movingAnti || t.antiOverlap);
    });
  };

  const renderEventContent = (eventInfo) => {
    const task = eventInfo.event.extendedProps.task;
    const parentTitle = eventInfo.event.extendedProps.parentTitle;
    const parentFlex = eventInfo.event.extendedProps.parentTaskId
      ? tasks.find(t => t.id === eventInfo.event.extendedProps.parentTaskId)?.flexMode
      : task.flexMode;
    const durationLabel = formatDurationLabel(task.duration);
    const taskProgress = getTaskTimeUnits(task);

    return (
      <div className={`fc-event-card ${task.completed ? 'is-completed' : ''}`}>
        <div className="fc-event-card-header">
          <input
            type="checkbox"
            checked={task.completed}
            onChange={() => onToggleComplete(task.id)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="fc-event-checkbox"
          />
          {parentTitle && <span className="fc-event-parent-marker">↳</span>}
          <span className="fc-event-title-text">{eventInfo.event.title}</span>
        </div>

        {parentTitle && <div className="fc-event-meta">{parentTitle}</div>}

        <div className="fc-event-badges">
          {task.priority === 'high' && (
            <span className="fc-event-badge fc-event-badge-high">
              <SafeIcon icon={FiFlag} />
              {t('calendar.highPriority')}
            </span>
          )}
          {durationLabel && (
            <span className="fc-event-badge">
              <SafeIcon icon={FiClock} />
              {durationLabel}
            </span>
          )}
          {taskProgress && (
            <span
              className="fc-event-badge"
              title={t('tasks.timeUnitsTooltip', { score: taskProgress.tuScore })}
            >
              {t(`tasks.timeUnits${taskProgress.tuStatus}`, {
                score: Math.abs(taskProgress.tuScore)
              })}
            </span>
          )}
        </div>

        {!parentTitle && task.subTasks && task.subTasks.length > 0 && !task.flexMode && (
          <div
            className="fc-event-subtasks"
            title={task.subTasks.map(st => st.title).join('\n')}
          >
            {task.subTasks.slice(0, 2).map(st => (
              <label
                key={st.id}
                className="fc-event-subtask"
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={st.completed}
                  onChange={() => onToggleComplete(st.id)}
                />
                <span>{st.title}</span>
              </label>
            ))}
            {task.subTasks.length > 2 && (
              <div className="fc-event-more">
                {t('tasks.moreSubtasks', { count: task.subTasks.length - 2 })}
              </div>
            )}
          </div>
        )}

        {parentFlex && (
          <div className="fc-event-meta text-purple-100">{t('calendar.flexModeActivated')}</div>
        )}
      </div>
    );
  };

  // Handle event drag
  const handleEventDrop = (info) => {
    const taskId = info.event.id;
    const newStartDate = normalizeDate(info.event.start);
    const newEndDate = normalizeDate(
      info.event.end || new Date(info.event.start.getTime() + (info.event.extendedProps.task?.duration || 60) * 60000)
    );

    if (hasOverlap(taskId, newStartDate, newEndDate)) {
      info.revert();
      alert(t('calendar.overlapAlert'));
      return;
    }

    // Update task dates
    updateTaskDates(taskId, newStartDate, newEndDate);
  };

  // Handle event resize
  const handleEventResize = (info) => {
    const taskId = info.event.id;
    const newStartDate = normalizeDate(info.event.start);
    const newEndDate = normalizeDate(info.event.end);

    if (hasOverlap(taskId, newStartDate, newEndDate)) {
      info.revert();
      alert(t('calendar.overlapAlert'));
      return;
    }

    // Update task dates
    updateTaskDates(taskId, newStartDate, newEndDate);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{t('calendar.title')}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t('calendar.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SafeIcon icon={FiCalendar} className="text-slate-500 dark:text-slate-400" />
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="dayGridMonth">{t('calendar.viewMonth')}</option>
            <option value="timeGridWeek">{t('calendar.viewWeek')}</option>
            <option value="timeGridThreeDay">{t('calendar.viewThreeDays')}</option>
            <option value="timeGridDay">{t('calendar.viewDay')}</option>
          </select>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFolders} className="text-slate-500 dark:text-slate-400" />
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              {currentProjectId
                ? t('calendar.showingProject', { projectName: projects.find(p => p.id === currentProjectId)?.name || 'Unknown Project' })
                : t('calendar.showingAllProjects')}
            </span>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-soft border border-slate-200/60 dark:border-slate-700/60 h-[calc(100vh-100px)] overflow-y-auto"
      >
        <div className={`calendar-container ${darkMode ? 'calendar-dark-mode' : ''}`}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={viewType}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            views={{
              timeGridThreeDay: {
                type: 'timeGrid',
                duration: { days: 3 },
                buttonText: '3 days'
              }
            }}
            events={calendarEvents}
            eventClick={handleEventClick}
            editable={true}
            eventStartEditable={true}
            eventDurationEditable={true}
            eventResizableFromStart={true}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventOverlap={(still, moving) => {
              const a = still.extendedProps.parentTaskId;
              const b = moving.extendedProps.parentTaskId;
              if (a && b && a === b) return false;
              return true;
            }}
            height="100%"
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:30:00"
            snapDuration="00:30:00"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: 'short'
            }}
            nowIndicator={true}
            dayMaxEvents={true}
            eventContent={renderEventContent}
            eventClassNames={(arg) => {
              const classes = [];
              if (arg.event.extendedProps.completed) {
                classes.push('event-completed');
              }
              if (arg.event.extendedProps.priority === 'high') {
                classes.push('event-high-priority');
              }
              return classes;
            }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default CalendarView;
